import React, { useState, useRef, useMemo, useCallback } from "react"
import { observer } from "mobx-react-lite"
import { TextInput, TextInputRef, DeviceEventEmitter, ScrollView } from "~/ui/primitives"
import styled, { useTheme } from "~/ui/styled"
import { StartFlex, BetweenFlex, CenterFlex, ColumnFlex, Button, Text, IconFont } from "~components/El"
import { NoData } from "~components/Layout"
import { Avatar } from "~widgets/avatar"
// import { OnlineStatus } from "~widgets/online-status"
import { useStores } from "~models"
import useGroupActions from "~hooks/useGroupActions"
import useNavigation from "~hooks/useNavigation"
import { globalToast } from "~utils"
import { REFRESH_CHANNEL_LIST_EVENT } from "~pages/app-stack/channel-list/constants"
import { Event, EventNames, FLAG_REPLY, RequestUserSearchData } from "~proto"
import { scrollViewFillStyle } from "~/ui/scroll-layout"

const Container = styled(ColumnFlex)`
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.blockBackground};
`

const SearchRow = styled(BetweenFlex)`
  flex-shrink: 0;
  padding: 12px;
  height: auto;
  width: 100%;
`

const ResultScroll = styled(ScrollView)`
  flex: 1;
  min-height: 0;
  width: 100%;
`

const SearchBar = styled(CenterFlex)`
  flex: 1;
  height: 40px;
  padding-right: 10px;
  margin-right: 12px;
  background-color: ${({ theme }) => theme.colors.inputBackground};
  border-radius: 20px;
`

const SearchInput = styled(TextInput)`
  flex: 1;
  min-width: 0;
  margin-left: 8px;
  border: none;
  outline: none;
  background: transparent;
`
const AddContact: React.FC = () => {
  const theme = useTheme()
  const navigation = useNavigation()
  const { eventStore, userStore, chatStore } = useStores()
  const { handleAddGroupMember, handleAddFriend, ensureLocalChatWhenJoiningGroup } = useGroupActions()
  const { currentUser } = userStore
  const [loading, setLoading] = useState(false)
  const [keyword, setKeyword] = useState("")
  const [contactList, setContactList] = useState([]) // 联系人列表
  const [chatGroupList, setChatGroupList] = useState([]) // 群组列表
  const [channelList, setChannelList] = useState([]) // 频道列表

  const searchInputRef = useRef<TextInputRef>(null)

  const isDataEmpty = useMemo(() => {
    return contactList.length === 0 && chatGroupList.length === 0 && channelList.length === 0
  }, [contactList, chatGroupList, channelList])

  // 搜索
  const handleSearch = useCallback(() => {
    if (!keyword) return
    searchInputRef?.current.blur()
    setLoading(true)
    const evt = new Event<RequestUserSearchData>(EventNames.REQUEST_USER_SEARCH, { name: keyword })
    eventStore.publish(evt, {
      flags: FLAG_REPLY,
      onData(data = {}) {
        // console.log("data", data)
        const { channel = [], group = [], users = []  } = data
        setContactList(users.map(i => {
          return {
            loading: false,
            ...i
          }
        }))
        setChatGroupList(group.map(i => {
          return {
            loading: false,
            ...i
          }
        }))
        setChannelList(channel.map(i => {
          return {
            loading: false,
            ...i
          }
        }))
        setLoading(false)
      },
      onError(error) {
        setContactList([])
        setChatGroupList([])
        setChannelList([])
        // console.log("error", error)
        globalToast.show(error?.message || "请求失败")
        setLoading(false)
      },
    })
  }, [keyword])

  const updateItemLoading = useCallback((id, loading) => {
    const updater = (prev) =>
      prev.map((item) => (item.id === id ? { ...item, loading } : item))

    setContactList(updater)
    setChatGroupList(updater)
    setChannelList(updater)
  }, [])

  // 添加好友
  const handleAddContact = useCallback(async (item) => {
    updateItemLoading(item.id, true)
    handleAddFriend({
      username: item.username,
      user_id: item.id
    }).then(() => {
      userStore.addUser({
        id: item.id,
        username: item.username,
        name: item.name,
        avatar: item.avatar,
      })
      const chat = chatStore.newPrivateChat({
        receiver_id: item.id,
        name: item.name,
      })
      chat.setDeleted(false)
      eventStore.publish(new Event(EventNames.CHAT_LIST, {}), { flags: FLAG_REPLY })
      updateItemLoading(item.id, false)
      globalToast.show("添加好友成功")
      navigation.goBack()
    }).catch(error => {
      updateItemLoading(item.id, false)
      globalToast.show(error?.message || "添加好友失败")
    })
  }, [chatStore, eventStore, handleAddFriend, navigation, updateItemLoading, userStore])

  // 加入群聊
  const handleAddGroup = useCallback((item) => {
    updateItemLoading(item.id, true)
    ensureLocalChatWhenJoiningGroup({
      id: item.id,
      chat_id: String(item.chat_id),
      title: item.title,
      image: item.image,
      isChannel: false,
    })
    handleAddGroupMember({
      chat_id: item.id,
      chat_cid: item.chat_id,
      user_ids: currentUser.id? [currentUser.id] : []
    }).then(() => {
      const chat =
        chatStore.getChatByMessageRef?.({
          chat_id: item.id,
          chat_cid: String(item.chat_id),
        }) ?? chatStore.byChatId?.(String(item.chat_id))
      chat?.setDeleted(false)
      eventStore.publish(new Event(EventNames.CHAT_LIST, {}), { flags: FLAG_REPLY })
      updateItemLoading(item.id, false)
      globalToast.show("加入群聊成功")
      navigation.goBack()
    }).catch(error => {
      updateItemLoading(item.id, false)
      globalToast.show(error?.message || "加入群聊失败")
    })
  }, [chatStore, currentUser.id, ensureLocalChatWhenJoiningGroup, eventStore, handleAddGroupMember, navigation, updateItemLoading])

  // 订阅频道
  const handleJoinChannel = useCallback((item) => {
    updateItemLoading(item.id, true)
    ensureLocalChatWhenJoiningGroup({
      id: item.id,
      chat_id: String(item.chat_id),
      title: item.title,
      image: item.image,
      isChannel: true,
    })
    handleAddGroupMember({
      chat_id: item.id,
      chat_cid: item.chat_id,
      user_ids: currentUser.id? [currentUser.id] : [],
      chat_type: 2
    }).then(() => {
      const chat =
        chatStore.getChatByMessageRef?.({
          chat_id: item.id,
          chat_cid: String(item.chat_id),
        }) ?? chatStore.byChatId?.(String(item.chat_id))
      chat?.setDeleted(false)
      eventStore.publish(new Event(EventNames.CHAT_LIST, {}), { flags: FLAG_REPLY })
      updateItemLoading(item.id, false)
      globalToast.show("频道订阅成功")
      DeviceEventEmitter.emit(REFRESH_CHANNEL_LIST_EVENT)
      navigation.goBack()
    }).catch(error => {
      updateItemLoading(item.id, false)
      globalToast.show(error?.message || "频道订阅失败")
    })
  }, [chatStore, currentUser.id, ensureLocalChatWhenJoiningGroup, eventStore, handleAddGroupMember, navigation, updateItemLoading])

  return (
    <Container>
      <SearchRow>
        <SearchBar>
          <IconFont
            name="ss"
            color={theme.colors.inputPlaceholderColor}
            size={12}
          />
          <SearchInput
            autoCapitalize="none"
            placeholder="输入手机号/邮箱/ID搜索"
            value={keyword}
            onChangeText={setKeyword}
            ref={searchInputRef}
            placeholderTextColor={theme.colors.inputPlaceholderColor}
            onSubmitEditing={handleSearch}
          />
        </SearchBar>

        <Button
          style={{
            width: "auto",
            height: 40,
            justifyContent: "center",
            paddingHorizontal: 24,
            borderRadius: 20,
            backgroundColor: theme.colors.primary,
          }}
          loading={loading}
          loadingText=""
          onClick={handleSearch}
        >
          <Text style={{ marginTop: 0, color: theme.colors.textLight, fontSize: 16 }}>搜索</Text>
        </Button>
      </SearchRow>
      {isDataEmpty && <NoData />}
      {!isDataEmpty && (
        <ResultScroll
          style={scrollViewFillStyle}
          onClick={() => searchInputRef.current?.blur()}
        >
          <ColumnFlex width="100%" padding="10px">
          <ColumnFlex width="100%">
           {contactList.length > 0 && (
              <StartFlex
                height="auto"
                padding="13px 0"
                style={{
                  width: "100%",
                  borderBottomWidth: 1,
                  borderBottomColor: theme.colors.border,
                }}
              >
                <Text style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text }}>
                  联系人
                </Text>
              </StartFlex>
           )}
            
            <ColumnFlex width="100%">
              {contactList.map((item) => (
                <BetweenFlex
                  width="100%"
                  key={item.id || item.username}
                  padding="10px 12px"
                >
                  <StartFlex flex={2} width="auto">
                    <Avatar
                      size={48}
                      name={item.name}
                      avatar={item.avatar}
                    />
                    <ColumnFlex flex={1} paddingLeft="10px">
                      <ColumnFlex>
                        <Text
                          style={{
                            color: theme.colors.text,
                            fontSize: 14,
                            fontWeight: 600,
                          }}
                        >
                          {item.name}
                        </Text>
                        <Text color={theme.colors.secondaryText} fontSize={12}>ID:{item.username}</Text>
                        
                        {/* <OnlineStatus
                          last_seen={item.last_seen}
                          online={item.online}
                        /> */}
                      </ColumnFlex>
                    </ColumnFlex>
                  </StartFlex>
                  <StartFlex flex={1} ml="20px" style={{ justifyContent: "flex-end" }}>
                    <Button
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "auto",
                        height: 40,
                        borderRadius: 8,
                        paddingHorizontal: 12,
                        backgroundColor: theme.colors.primary,
                      }}
                      loading={item.loading}
                      loadingText="请求中..."
                      disabled={item.loading || item.is_joined}
                      onClick={() => handleAddContact(item)}
                    >
                      <IconFont
                        name="tjhy"
                        size={14}
                        color={theme.colors.textLight}
                      />
                      <Text
                        height="auto"
                        style={{
                          color: theme.colors.textLight,
                          fontSize: 14,
                          fontWeight: 600,
                          marginTop: 0,
                        }}
                      >
                        {item.is_joined ? "已添加" : "添加好友"}
                      </Text>
                    </Button>
                  </StartFlex>
                </BetweenFlex>
              ))}
            
            </ColumnFlex>
          </ColumnFlex>
          <ColumnFlex width="100%">
           {chatGroupList.length > 0 && (
              <StartFlex
                padding="13px 0"
                style={{
                  width: "100%",
                  borderBottomWidth: 1,
                  borderBottomColor: theme.colors.border,
                }}
              >
                <Text
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  群聊
                </Text>
              </StartFlex>
           )}
            
            <ColumnFlex>
              {chatGroupList.map((item) => (
                <BetweenFlex
                  key={item.id || item.username}
                  style={{
                    // height:58,
                    paddingHorizontal: 12,
                    paddingVertical: 10,
                  }}
                >
                  <StartFlex flex={2}>
                    <Avatar
                      size={48}
                      name={item.title}
                      avatar={item.image}
                    />
                    <ColumnFlex flex={1} paddingLeft="10px">
                      <StartFlex>
                        <Text
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            marginRight: 10,
                          }}
                        >
                          {item.title}
                        </Text>
                        <Text
                          style={{
                            color: theme.colors.primaryText,
                            fontSize: 12,
                          }}
                        >
                          群聊
                        </Text>
                      </StartFlex>
                      <StartFlex padding="2px 0">
                        <IconFont
                          name={"cjql"}
                          size={14}
                          color={theme.colors.infoText}
                        />
                        <Text
                          style={{
                            color: theme.colors.infoText,
                            paddingLeft: 4,
                          }}
                        >
                          {item.member_count || '0'}人
                        </Text>
                      </StartFlex>
                      {item.desc && (
                        <StartFlex>
                          <Text
                            numberOfLines={1}
                            style={{
                              color: theme.colors.infoText,
                              fontSize: 12,
                            }}
                          >
                            {item.desc || ""}
                          </Text>
                        </StartFlex>
                      )}
                      
                    </ColumnFlex>
                  </StartFlex>
                  <StartFlex flex={1} ml="20px" style={{ justifyContent: "flex-end" }}>
                    <Button
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "center",
                        height: 40,
                        borderRadius: 8,
                        paddingHorizontal: 12,
                        backgroundColor: theme.colors.primary,
                      }}
                      loading={item.loading}
                      loadingText="请求中..."
                      disabled={item.loading || item.is_joined}
                      onClick={() => handleAddGroup(item)}
                    >
                      <IconFont
                        name="tjhy"
                        size={14}
                        color={theme.colors.textLight}
                      />
                      <Text
                        height="auto"
                        style={{
                          color: theme.colors.textLight,
                          fontSize: 14,
                          fontWeight: 600,
                          marginTop: 0,
                        }}
                      > 
                        {item.is_joined ? "已加入群" : "加入群聊"}
                      </Text>
                    </Button>
                  </StartFlex>
                  
                </BetweenFlex>
              ))}
              
            </ColumnFlex>
          </ColumnFlex>
          <ColumnFlex width="100%">
            {channelList.length  > 0 && (
              <StartFlex
                padding="13px 0"
                style={{
                  width: "100%",
                  borderBottomWidth: 1,
                  borderBottomColor: theme.colors.border,
                }}
              >
                <Text
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  频道
                </Text>
              </StartFlex>
            )}
            
            <ColumnFlex width="100%">
              {channelList.map((item) => (
                <BetweenFlex
                  key={item.id || item.username}
                  style={{
                    // height:58,
                    // paddingHorizontal: 12,
                    paddingVertical: 10,
                  }}
                >
                  <StartFlex flex={2}>
                    <Avatar
                      size={48}
                      name={item.title}
                      avatar={item.image}
                    />
                    <ColumnFlex flex={1} paddingLeft="10px">
                      <StartFlex>
                        <Text
                          style={{
                            color: theme.colors.text,
                            fontSize: 14,
                            fontWeight: 600,
                            marginRight: 10,
                          }}
                        >
                          {item.title}
                        </Text>
                        <Text
                          style={{
                            color: theme.colors.primaryText,
                            fontSize: 12,
                          }}
                        >
                          频道
                        </Text>
                      </StartFlex>
                      <StartFlex padding="2px 0">
                        <IconFont
                          name={"cjql"}
                          size={12}
                          color={theme.colors.infoText}
                        />
                        <Text
                          style={{
                            color: theme.colors.infoText,
                            paddingLeft: 4,
                          }}
                        >
                          {item.member_count || '0'}订阅
                        </Text>
                      </StartFlex>
                      {item.desc && (
                        <StartFlex>
                          <Text
                            numberOfLines={1}
                            style={{
                              color: theme.colors.infoText,
                              fontSize: 12,
                            }}
                          >
                            {item.desc || ""}
                          </Text>
                        </StartFlex>
                      )}
                      
                    </ColumnFlex>
                  </StartFlex>
                  <StartFlex flex={1} ml="20px" style={{ justifyContent: "flex-end" }}>
                    <Button
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "auto",
                        height: 40,
                        borderRadius: 8,
                        paddingHorizontal: 12,
                        backgroundColor: theme.colors.primary,
                      }}
                      loading={item.loading}
                      loadingText="请求中..."
                      disabled={item.loading || item.is_joined}
                      onClick={() => handleJoinChannel(item)}
                    >
                      <IconFont
                        name="tjhy"
                        size={14}
                        color={theme.colors.textLight}
                      />
                      <Text
                        height="auto"
                        style={{
                          color: theme.colors.textLight,
                          fontSize: 14,
                          fontWeight: 600,
                          marginTop: 0,
                        }}
                      >
                        {item.is_joined ? "已订阅" : "订阅频道"}
                      </Text>
                    </Button>
                  </StartFlex>
                 
                </BetweenFlex>
              ))}
              
            </ColumnFlex>
          </ColumnFlex>
          </ColumnFlex>
        </ResultScroll>
      )}
    </Container>
  )
}

export default observer(AddContact)
